/*
 * Copyright Innov'ATM all rights reserved.
 * This software is the property of Innov'ATM and may not be used in any manner except under a
 * license agreement signed with Innov'ATM.
 */

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import { isBefore, startOfDay, subDays } from 'date-fns';
import { useFormikContext } from 'formik';
import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { FlightTimeType, Resource, ResourceType } from '../../../../../../../backend';
import { getTwoDaysAgo } from '../../../../../../../utils/utils';
import { Select } from '../../../../../../form/Select/Select';
import { selectSx } from '../ResourcesSelect.style';
import { TurnaroundFormValues } from '../TurnaroundForm.utils';
import { ParkingTowingInput } from './ParkingTowingInput';
import {
    StyledErrorContainer,
    StyledSquareIconButton,
    StyledTowingControls,
    StyledTowingsContainer,
} from './ParkingTowings.style';

export const ParkingTowings = memo(function ParkingTowings({
    allResources,
    disabled,
    isArrDayInPast,
}: {
    allResources: Resource[];
    disabled?: boolean;
    isArrDayInPast?: boolean;
}) {
    const { values, setValues, errors } = useFormikContext<TurnaroundFormValues>();

    const parkings = allResources?.filter(r => r.type === ResourceType.PARKING && r.active);
    const towings = values.turnaround.arrivalFlight?.towings;

    const isDryDeparture = !values.turnaround.arrivalFlight && !!values.turnaround.departureFlight;
    const initialParking = isDryDeparture
        ? values.turnaround.departureFlight?.departureParking
        : values.turnaround.arrivalFlight?.arrivalParking;

    const handleInitialParkingChange = useCallback(
        (event: SelectChangeEvent) => {
            setValues({
                ...values,
                turnaround: {
                    ...values.turnaround,
                    departureFlight: isDryDeparture
                        ? {
                              ...values.turnaround.departureFlight,
                              departureParking: event.target.value,
                          }
                        : values.turnaround.departureFlight,
                    arrivalFlight: isDryDeparture
                        ? values.turnaround.arrivalFlight
                        : {
                              ...values.turnaround.arrivalFlight,
                              arrivalParking: event.target.value,
                          },
                },
                resourceTypes: [...values.resourceTypes, ResourceType.PARKING],
            });
        },
        [isDryDeparture, setValues, values],
    );

    const handleAddTowing = useCallback(() => {
        const newTowings = [...towings];
        const earliestPossibleTime =
            towings && towings.length > 0
                ? towings[towings.length - 1].time.time + 1200000
                : values.turnaround.arrivalFlight?.actualResourceOccupations?.['PARKING']?.occupationStart.time +
                      1200000 || Date.now();
        const towingInitialTime = isBefore(earliestPossibleTime, getTwoDaysAgo())
            ? startOfDay(subDays(new Date(), 1)).getTime()
            : earliestPossibleTime;
        newTowings.push({
            time: {
                // add 20 minutes to last towing time or use the earliest possible time if no towing
                time: towingInitialTime,
                // default to E to indicate, will be changed to T upon form submit
                type: FlightTimeType.E,
            },
            parking: '',
        });
        setValues({
            ...values,
            turnaround: {
                ...values.turnaround,
                arrivalFlight: {
                    ...values.turnaround.arrivalFlight,
                    towings: newTowings,
                },
            },
        });
    }, [setValues, towings, values]);

    const handleRemoveLastTowing = useCallback(() => {
        const newTowings = [...towings];
        newTowings.pop();
        setValues({
            ...values,
            turnaround: {
                ...values.turnaround,
                arrivalFlight: {
                    ...values.turnaround.arrivalFlight,
                    towings: newTowings,
                },
            },
        });
    }, [setValues, towings, values]);

    return (
        <div style={{ position: 'relative' }}>
            <StyledTowingsContainer>
                <Select
                    value={initialParking || ''}
                    onChange={handleInitialParkingChange}
                    label={<>PARKING{towings?.length > 0 && 'S'}</>}
                    FormControlProps={{
                        sx: { ...selectSx, maxWidth: '100px', minWidth: '75px' },
                    }}
                    disabled={disabled || isArrDayInPast}
                >
                    {!parkings?.find(r => r.name === initialParking) && (
                        <MenuItem value={initialParking} disabled={true}>
                            <em>{initialParking}</em>
                        </MenuItem>
                    )}
                    <MenuItem value={''}>
                        <em>-</em>
                    </MenuItem>
                    {parkings?.map(({ name }) => (
                        <MenuItem key={name} value={name}>
                            <FormattedMessage id={`turnaround.popin.field`} values={{ field: name }} />
                        </MenuItem>
                    ))}
                </Select>
                {towings?.map((towing, index) => (
                    <ParkingTowingInput
                        key={index}
                        parkings={parkings}
                        towings={towings}
                        index={index}
                        disabled={disabled || isDryDeparture}
                    />
                ))}
                <StyledTowingControls>
                    <StyledSquareIconButton
                        onClick={handleAddTowing}
                        disabled={disabled || isDryDeparture || towings?.length >= 3}
                    >
                        <AddIcon sx={{ color: 'white', width: '15px', height: '15px' }} />
                    </StyledSquareIconButton>
                    <StyledSquareIconButton
                        onClick={handleRemoveLastTowing}
                        disabled={disabled || isDryDeparture || !towings?.length}
                        className={'test'}
                    >
                        <ClearIcon sx={{ color: 'white', width: '15px', height: '15px' }} />
                    </StyledSquareIconButton>
                </StyledTowingControls>
            </StyledTowingsContainer>
            {errors[`turnaround.arrivalFlight.towings`] && (
                <StyledErrorContainer>
                    <FormattedMessage id={errors[`turnaround.arrivalFlight.towings`]} />
                </StyledErrorContainer>
            )}
        </div>
    );
});
